import { currencyInfo } from './currency.ts';

export type ParseMoneyError = 'EMPTY' | 'INVALID' | 'TOO_MANY_DECIMALS';

export type ParseMoneyResult =
  | { readonly ok: true; readonly minor: bigint }
  | { readonly ok: false; readonly error: ParseMoneyError };

// Everything that is not a digit, sign or separator: currency symbols and codes typed by hand.
const DECORATION = /[£$€¥]|zł|PLN|GBP|EUR|USD|CHF/gi;
// Regular, no-break, narrow no-break and thin spaces, and apostrophes (Swiss grouping).
const GROUPING_SPACE = /[\s\u00a0\u202f\u2009']/g;

/**
 * Parses user input into minor units without floating point.
 * Accepts "19,99", "19.99", "1 234,56", "1,234.56", "£12", "-4.5", "12,5 zł".
 * With a single separator the locale decides only when it is ambiguous (exactly three digits
 * after it): in Polish "1.234" means 1234, in English "1,234" means 1234.
 * More decimals than the currency allows is an error, never a silent rounding.
 */
export function parseMoneyInput(input: string, currency: string, locale: string): ParseMoneyResult {
  const scale = currencyInfo(currency).scale;
  let text = input.replace(DECORATION, '').replace(GROUPING_SPACE, '');
  if (text === '') return { ok: false, error: 'EMPTY' };

  let negative = false;
  if (text.startsWith('-') || text.startsWith('−')) {
    negative = true;
    text = text.slice(1);
  } else if (text.startsWith('+')) {
    text = text.slice(1);
  }
  if (!/^[\d.,]+$/.test(text) || !/\d/.test(text)) return { ok: false, error: 'INVALID' };

  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  let decimalSeparator: ',' | '.' | null = null;

  if (lastComma >= 0 && lastDot >= 0) {
    decimalSeparator = lastComma > lastDot ? ',' : '.';
  } else if (lastComma >= 0 || lastDot >= 0) {
    const separator = lastComma >= 0 ? ',' : '.';
    const occurrences = text.split(separator).length - 1;
    const digitsAfter = text.length - text.lastIndexOf(separator) - 1;
    const polish = locale.toLowerCase().startsWith('pl');
    const localeDecimal = polish ? ',' : '.';
    if (occurrences > 1) {
      decimalSeparator = null; // repeated separator can only be grouping: "1.234.567"
    } else if (digitsAfter === 3 && separator !== localeDecimal) {
      decimalSeparator = null; // "1,234" in English, "1.234" in Polish
    } else {
      decimalSeparator = separator;
    }
  }

  let whole = text;
  let fraction = '';
  if (decimalSeparator !== null) {
    const index = text.lastIndexOf(decimalSeparator);
    whole = text.slice(0, index);
    fraction = text.slice(index + 1);
  }
  // Grouping, when present, must be well-formed: 1-3 digits, then groups of exactly three.
  if (/[.,]/.test(whole)) {
    if (!/^\d{1,3}([.,]\d{3})+$/.test(whole)) return { ok: false, error: 'INVALID' };
    const separators = new Set(whole.replace(/\d/g, ''));
    if (separators.size !== 1 || separators.has(decimalSeparator ?? '')) {
      return { ok: false, error: 'INVALID' };
    }
    whole = whole.replace(/[.,]/g, '');
  }
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction)) return { ok: false, error: 'INVALID' };
  if (whole === '' && fraction === '') return { ok: false, error: 'INVALID' };

  const trimmedFraction = fraction.replace(/0+$/, '');
  if (trimmedFraction.length > scale) return { ok: false, error: 'TOO_MANY_DECIMALS' };

  const minorText = (whole === '' ? '0' : whole) + trimmedFraction.padEnd(scale, '0');
  const minor = BigInt(minorText);
  return { ok: true, minor: negative ? -minor : minor };
}
